import { createEmptyRegistration, executeAction, isRegistrationValid } from './registrationHelpers';

/**
 * The three sub-registrations of a registration group, mirroring the legacy
 * `RegistrationGroup` model: work (time, type 0), product/cost (type 1) and
 * distance/travel (type 2). Shared header fields (relation, date, assignment, task,
 * reference year, remarks, interaction) are kept in sync across all three.
 */
export const GROUP_PARTS = ['work', 'cost', 'travel'];

export const PART_TYPE = { work: 0, cost: 1, travel: 2 };

/** Actions whose value is a shared header field and must fan out to every sub-registration. */
export const SHARED_ACTIONS = new Set([
  'dateChanged',
  'referenceYearChanged',
  'relationChanged',
  'invoiceStreamsLoaded',
  'assignmentChanged',
  'taskChanged',
  'remarkChanged',
  'internalRemarkChanged',
  'interactionTypeChanged',
  'interactionTitleChanged',
  'interactionDescriptionChanged',
  'interactionInit',
]);

export const createEmptyGroup = (seed = {}) => {
  const parts = {};
  GROUP_PARTS.forEach(part => {
    parts[part] = createEmptyRegistration({ ...seed, type: PART_TYPE[part] });
  });
  return parts;
};

// Header fields that are shared across the three sub-registrations of a group.
const COMMON_FIELDS = [
  'relation',
  'invoiceStreamId',
  'assignmentTemplateId',
  'plannedTaskId',
  'plannedTaskTemplateId',
  'plannedTaskTemplateType',
  'plannedTaskName',
  'mainPlannedTaskId',
  'mainTaskTemplateId',
  'mainPlannedTaskName',
  'referenceYear',
  'date',
  'userId',
  'group',
  'remark',
  'internalRemark',
  'interactionType',
  'interactionTitle',
  'interactionDescription',
  'interactionId',
];

const regType = reg => reg.type ?? reg.resource?.type;

/**
 * Builds a group for editing from all registrations that belong to it (the siblings created
 * together in one dialog). Each registration fills its own type's part; any missing part
 * starts empty but inherits the shared header fields (relation, date, task, remarks, …).
 */
export const createGroupFromRegistrations = (registrations = []) => {
  const [first = {}] = registrations;
  const common = {};
  COMMON_FIELDS.forEach(field => {
    if (first[field] !== undefined) common[field] = first[field];
  });
  const parts = {};
  GROUP_PARTS.forEach(part => {
    const match = registrations.find(reg => regType(reg) === PART_TYPE[part]);
    parts[part] = match
      ? createEmptyRegistration({ ...match, type: PART_TYPE[part] })
      : createEmptyRegistration({ ...common, type: PART_TYPE[part] });
  });
  return parts;
};

/** Whether any part of the group is a persisted registration (i.e. we're editing). */
export const groupHasExisting = group => GROUP_PARTS.some(part => !!group[part].id);

/** Applies a shared header change to every sub-registration. */
export const applySharedAction = (group, action, value) => {
  const next = {};
  GROUP_PARTS.forEach(part => {
    next[part] = executeAction(group[part], action, value);
  });
  return next;
};

/** Applies a change to a single sub-registration (its own resource, price, quantity, …). */
export const applyPartAction = (group, part, action, value) => ({
  ...group,
  [part]: executeAction(group[part], action, value),
});

/** A sub-registration counts as filled once a resource has been picked for it. */
export const partHasContent = reg => !!(reg && reg.resource && reg.resource.id);

/** Builds the `[work, cost, travel]` payload the API expects, dropping empty parts. */
export const buildGroupPayload = group =>
  GROUP_PARTS.map(part => group[part])
    .filter(partHasContent)
    .map(reg => {
      const model = { ...reg };
      delete model.orgInteraction;
      return model;
    });

/** The group is valid when at least one part is filled and every filled part is valid. */
export const isGroupValid = (group, options) => {
  const filled = GROUP_PARTS.map(part => group[part]).filter(partHasContent);
  if (!filled.length) return false;
  return filled.every(reg => isRegistrationValid(reg, options));
};

/** The first filled part, or the work part, used as the representative for shared fields. */
export const groupRepresentative = group =>
  GROUP_PARTS.map(part => group[part]).find(partHasContent) || group.work;
